/* eslint-disable @spaced-out/i18n/no-static-labels */
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import matchSorter from 'match-sorter';
import type {ActionAttributeSchema} from 'src/types/action-based-targeting-meta';
import {classify} from 'src/utils/classify';

import {
  Table,
  BasicSingleCell,
  DoubleCell,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {BodyMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import css from './abt-table.css';

// @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type. | TS7031 - Binding element 'extras' implicitly has an 'any' type.
const Variable = ({data, extras}) => {
  const isSelected = extras?.selectedKeys.includes(data.id);
  return (
    <DoubleCell
      title={data.label}
      subtitle={data.eventGroup}
      className={classify(css.variableSection, {[css.highlight]: isSelected})}
    />
  );
};

// @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type. | TS7031 - Binding element 'extras' implicitly has an 'any' type.
const Help = ({data, extras}) => {
  const isSelected = extras?.selectedKeys.includes(data.id);
  return (
    <BasicSingleCell
      className={classify(css.helpSection, {[css.highlight]: isSelected})}
    >
      <BodyMedium className={css.helpSectionContent} color="secondary">
        {data.descriptionOne}
      </BodyMedium>
    </BasicSingleCell>
  );
};

// @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type. | TS7031 - Binding element 'extras' implicitly has an 'any' type.
const Selection = ({data, extras}) => {
  const {t} = useLabelTranslation();

  const isSelected = extras?.selectedKeys.includes(data.id);
  return (
    <BasicSingleCell
      className={classify(css.selectionSection, {[css.highlight]: isSelected})}
    >
      <Button
        onClick={() => {
          extras?.selectedKeys.includes(data.id)
            ? extras?.handleSelect(
                // @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
                extras.selectedKeys.filter((id) => id !== data.id),
              )
            : extras?.handleSelect([...extras.selectedKeys, data.id]);
        }}
        size="small"
        type="tertiary"
      >
        {extras?.selectedKeys.includes(data.id)
          ? t('Unselect', 'Unselect')
          : t('Select', 'Select')}
      </Button>
    </BasicSingleCell>
  );
};

const getHeaders = (t: (key: string, defaultValue: string) => string) => [
  {
    label: t('Variable_Name', 'Variable Name'),
    key: 'label',
    sortable: true,
    render: Variable,
    className: css.secondaryBackground,
  },
  {label: '', key: 'help', render: Help, className: css.secondaryBackground},
  {
    label: '',
    key: 'id',
    render: Selection,
    sortable: false,
    className: css.secondaryBackground,
  },
];

type AbtTableProps = {
  query: string;
  selected: string[];
  handleSelect: (arg1: string[]) => void;
  filters: Map<string, Array<string>>;
  abtAttributes: ActionAttributeSchema[];
  isLoading: boolean;
};

// @ts-expect-error - TS7006 - Parameter 'tableItems' implicitly has an 'any' type.
const getFilteredItems = (tableItems, filters: Map<string, Array<string>>) => {
  const entityTypeFilter = filters.get('entityType') || [];
  if (entityTypeFilter.length === 0) {
    return tableItems;
  }
  // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
  return tableItems.filter((item) =>
    entityTypeFilter.includes(item.eventGroup),
  );
};

const AbtTable = ({
  query,
  selected,
  handleSelect,
  filters,
  abtAttributes,
  isLoading = false,
}: AbtTableProps) => {
  const {t} = useLabelTranslation();
  const headers = getHeaders(t);
  const tableItems = abtAttributes.map(
    ({label, value, eventGroup, descriptionOne, descriptionTwo}) => ({
      label,
      id: value,
      eventGroup,
      descriptionOne,
      descriptionTwo,
    }),
  );
  const filteredItems = getFilteredItems(tableItems, filters);
  const matchingEntries = matchSorter(filteredItems, query, {keys: ['label']});
  if (abtAttributes.length === 0 || isLoading) {
    return (
      <div className={css.loadingContainer}>
        <CircularLoader size="medium" />
      </div>
    );
  }
  return (
    <Table
      borderRadius="0px"
      classNames={{
        wrapper: classify(css.unsetBorders, {
          [css.unsetBottomBorder]:
            matchingEntries.length > 4 ||
            isLoading ||
            matchingEntries.length === 0,
        }),
      }}
      key={matchingEntries.length}
      // @ts-expect-error - TS2322 - Type '({ label: string; key: string; sortable: boolean; render: ({ data, extras }: { data: any; extras: any; }) => Element; className: string; } | { label: string; key: string; render: ({ data, extras }: { data: any; extras: any; }) => Element; className: string; sortable?: undefined; })[]' is not assignable to type 'GenericHeaderItems<any, { selectedKeys: string[]; handleSelect: (arg1: string[]) => void; }>'.
      headers={headers}
      tableHeaderClassName={css.stickyTableHeader}
      entries={matchingEntries}
      onSelect={handleSelect}
      emptyText={
        <EmptyState
          description="We cant find any items matching your search."
          title="Sorry! No result found"
          imageVariant="file"
        />
      }
      extras={{
        selectedKeys: [...selected],
        handleSelect,
      }}
    />
  );
};

export default AbtTable;
